import {
  MonthEndNetworkTotalBasis,
  type MonthEndEntry,
  type MonthEndReport,
} from '@urbanx/agx.rex.client';

export const monthEndAwardLabels: Record<string, string> = {
  'Platinum agency GCI': '#1 Plat tier',
  'Gold agency GCI': '#1 Gold tier',
  'Silver agency GCI': '#1 Silver tier',
  'Highest listings — Individual/Team': 'Highest # of Listings – Individual',
  'Highest listings — Agency': 'Highest # of Listings – Agency',
  'Highest contracts — Individual/Team': 'Highest # Contracts – Individual',
  'Highest contracts — Agency': 'Highest # Contracts – Agency',
  'Highest property sale price': 'Highest Property Sale Price',
};

const wholeDollars = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
  maximumFractionDigits: 0,
});

export const monthEndCurrency = (value: number) => wholeDollars.format(value);

export const monthEndEmailSections = (report: MonthEndReport) => {
  const ranking = (title: string, entries: MonthEndEntry[]) => ({
    title,
    lines: entries.map((entry) => ({
      text: `${entry.name} - ${monthEndCurrency(entry.value)}`,
      place: entry.place,
    })),
  });
  const awards = Object.entries(monthEndAwardLabels).map(([name, title]) => {
    const award = report.awards.find((item) => item.name === name);
    return {
      title,
      lines: (award?.winners ?? []).map((entry) => ({
        text: `${entry.sellingAgents?.length ? entry.sellingAgents.join('; ') : entry.name} – ${award?.unit === 'Currency' ? monthEndCurrency(entry.value) : entry.value.toLocaleString('en-AU')}`,
        place: undefined,
      })),
    };
  });
  const total = report.networkTotals.find(
    (item) => item.basis === MonthEndNetworkTotalBasis.Unconditional
  );
  return [
    ranking(
      '#1 Agent (GCI)',
      report.individualTeamGciRankings.filter((entry) => entry.place === 1)
    ),
    ranking(
      '#1 Agency (GCI)',
      report.agencyGciRankings.filter((entry) => entry.place === 1)
    ),
    ranking('Top 10 Agents (GCI)', report.individualTeamGciRankings),
    ranking('Top 10 Agencies (GCI)', report.agencyGciRankings),
    ...awards,
    {
      title: 'Network Total Sales Volume',
      lines: total ? [{ text: monthEndCurrency(total.salesVolume), place: undefined }] : [],
    },
    {
      title: 'Network Total GCI',
      lines: total ? [{ text: monthEndCurrency(total.gci), place: undefined }] : [],
    },
  ];
};

const escapeHtml = (value: string) =>
  value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');

export const monthEndEmail = (report: MonthEndReport) => {
  const sections = monthEndEmailSections(report);
  return {
    text: sections
      .map(
        ({ title, lines }) =>
          `${title}\n${lines.length ? lines.map((line) => `${line.place === undefined ? '' : `${line.place}. `}${line.text}`).join('\n') : 'No qualifying winner'}`
      )
      .join('\n\n'),
    html: `<div style="font-family:Arial,sans-serif;font-size:14px;color:#222">${sections.map(({ title, lines }) => `<div style="margin-bottom:20px"><strong>${escapeHtml(title)}</strong>${lines[0]?.place !== undefined ? `<ol style="margin-top:0;margin-bottom:0">${lines.map((line) => `<li value="${line.place}">${escapeHtml(line.text)}</li>`).join('')}</ol>` : `<div>${lines.length ? lines.map((line) => escapeHtml(line.text)).join('<br>') : 'No qualifying winner'}</div>`}</div>`).join('')}</div>`,
  };
};
